/* eslint-disable react-hooks/refs */
// refs used intetnionally in this file

'use client'

import type {
  AlgoliaInsightsConfig,
  AlgoliaInsightsProviderProps,
} from '@maestro/algolia-insights/types'
import type { Subscription } from 'rxjs'

import { useEffect, useRef, useState } from 'react'

import { initInsights } from '@maestro/algolia-insights/algolia-insights-client'
import { getAlgoliaInsightsConfig } from '@maestro/algolia-insights/config'
import {
  handleArticleClicked,
  handleArticleListViewed,
  handleArticleViewed,
  handleCollectAtStoreOrderCompleted,
  handleCookieConsentDeclinedEvent,
  handleCookieConsentDialogOpenedEvent,
  handleCookieConsentGrantedEvent,
  handleCookieConsentSetEvent,
  handleOrderCompleted,
  handleProductAddedToCart,
  handleProductAddedToWishlist,
  handleProductClicked,
  handleProductListViewed,
  handleProductsFiltered,
  handleProductSubscribedToOutOfStock,
  handleProductViewed,
} from '@maestro/algolia-insights/event-handlers'
import { useEventBusContext } from '@maestro/core/event/event-bus.context'
import { createLogger } from '@maestro/core/logger'
import { type EventPayloadType, EventType } from '@maestro/data-layer'

const log = createLogger('maestro:algolia-insights')

/**
 * Algolia Insights Provider
 * Subscribes to EventBus events and sends analytics to Algolia
 */
export function AlgoliaInsightsProvider({
  children,
}: AlgoliaInsightsProviderProps) {
  const { subscribeTo } = useEventBusContext()
  const subscriptionsRef = useRef<Subscription[]>([])
  const isSubscribedRef = useRef(false)
  const configRef = useRef<AlgoliaInsightsConfig | null>(null)
  const [isConsentGranted, setIsConsentGranted] = useState<boolean>(false)
  const [isCookieConsentDialogOpened, setIsCookieConsentDialogOpened] =
    useState<boolean>(false)

  // Create context object containing both state and setters
  const algoliaInsightsContext = {
    isConsentGranted,
    isCookieConsentDialogOpened,
    setIsConsentGranted,
    setIsCookieConsentDialogOpened,
  }
  const algoliaInsightsContextRef = useRef(algoliaInsightsContext)

  // Update ref whenever context changes
  algoliaInsightsContextRef.current = algoliaInsightsContext

  // Get configuration and initialize (only once)
  if (!configRef.current) {
    try {
      configRef.current = getAlgoliaInsightsConfig()

      if (!configRef.current.isEnabled) {
        if (configRef.current.isDebug) {
          log.info('[Algolia Insights] Tracking is disabled')
        }
      } else {
        initInsights(
          configRef.current.appId,
          configRef.current.apiKey,
          configRef.current.isDebug
        )
      }
    } catch (error) {
      log.error('[Algolia Insights] Configuration error:', error)
    }
  }

  const config = configRef.current

  // Subscribe synchronously during render (only once)
  if (!isSubscribedRef.current && config?.isEnabled) {
    isSubscribedRef.current = true

    const productListViewedSub = subscribeTo<
      EventPayloadType[EventType.PRODUCT_LIST_VIEWED]
    >(
      EventType.PRODUCT_LIST_VIEWED,
      'algolia-insights:product-list-viewed'
    )?.subscribe((data) =>
      handleProductListViewed(data, config, algoliaInsightsContextRef)
    )

    const productClickedSub = subscribeTo<
      EventPayloadType[EventType.PRODUCT_CLICKED]
    >(EventType.PRODUCT_CLICKED, 'algolia-insights:product-clicked')?.subscribe(
      (data) => handleProductClicked(data, config, algoliaInsightsContextRef)
    )

    const productViewedSub = subscribeTo<
      EventPayloadType[EventType.PRODUCT_VIEWED]
    >(EventType.PRODUCT_VIEWED, 'algolia-insights:product-viewed')?.subscribe(
      (data) => handleProductViewed(data, config, algoliaInsightsContextRef)
    )

    const productAddedToCartSub = subscribeTo<
      EventPayloadType[EventType.PRODUCT_ADDED_TO_CART]
    >(
      EventType.PRODUCT_ADDED_TO_CART,
      'algolia-insights:product-added-to-cart'
    )?.subscribe((data) =>
      handleProductAddedToCart(data, config, algoliaInsightsContextRef)
    )

    const productAddedToWishlistSub = subscribeTo<
      EventPayloadType[EventType.PRODUCT_ADDED_TO_WISHLIST]
    >(
      EventType.PRODUCT_ADDED_TO_WISHLIST,
      'algolia-insights:product-added-to-wishlist'
    )?.subscribe((data) =>
      handleProductAddedToWishlist(data, config, algoliaInsightsContextRef)
    )

    const orderCompletedSub = subscribeTo<
      EventPayloadType[EventType.ORDER_COMPLETED]
    >(EventType.ORDER_COMPLETED, 'algolia-insights:order-completed')?.subscribe(
      (data) => handleOrderCompleted(data, config, algoliaInsightsContextRef)
    )

    const collectAtStoreOrderCompletedSub = subscribeTo<
      EventPayloadType[EventType.COLLECT_AT_STORE_ORDER_COMPLETED]
    >(
      EventType.COLLECT_AT_STORE_ORDER_COMPLETED,
      'algolia-insights:collect-at-store-order-completed'
    )?.subscribe((data) =>
      handleCollectAtStoreOrderCompleted(
        data,
        config,
        algoliaInsightsContextRef
      )
    )

    const productSubscribedToOutOfStockSub = subscribeTo<
      EventPayloadType[EventType.PRODUCT_SUBSCRIBED_TO_OUT_OF_STOCK]
    >(
      EventType.PRODUCT_SUBSCRIBED_TO_OUT_OF_STOCK,
      'algolia-insights:product-subscribed-to-out-of-stock'
    )?.subscribe((data) =>
      handleProductSubscribedToOutOfStock(
        data,
        config,
        algoliaInsightsContextRef
      )
    )

    const productsFilteredSub = subscribeTo<
      EventPayloadType[EventType.PRODUCTS_FILTERED]
    >(
      EventType.PRODUCTS_FILTERED,
      'algolia-insights:products-filtered'
    )?.subscribe((data) =>
      handleProductsFiltered(data, config, algoliaInsightsContextRef)
    )

    const articleClickedSub = subscribeTo<
      EventPayloadType[EventType.ARTICLE_CLICKED]
    >(EventType.ARTICLE_CLICKED, 'algolia-insights:article-clicked')?.subscribe(
      (data) => handleArticleClicked(data, config, algoliaInsightsContextRef)
    )

    const articleViewedSub = subscribeTo<
      EventPayloadType[EventType.ARTICLE_VIEWED]
    >(EventType.ARTICLE_VIEWED, 'algolia-insights:article-viewed')?.subscribe(
      (data) => handleArticleViewed(data, config, algoliaInsightsContextRef)
    )

    const articleListViewedSub = subscribeTo<
      EventPayloadType[EventType.ARTICLE_LIST_VIEWED]
    >(
      EventType.ARTICLE_LIST_VIEWED,
      'algolia-insights:article-list-viewed'
    )?.subscribe((data) =>
      handleArticleListViewed(data, config, algoliaInsightsContextRef)
    )

    const cookieConsentSetSubscription = subscribeTo<
      EventPayloadType[EventType.COOKIE_CONSENT_SET]
    >(
      EventType.COOKIE_CONSENT_SET,
      'ALGOLIA_INSIGHTS_COOKIE_CONSENT_SET_EVENT'
    )?.subscribe((payload) => {
      handleCookieConsentSetEvent(payload, config, algoliaInsightsContextRef)
    })

    const cookieConsentDeclinedSubscription = subscribeTo<
      EventPayloadType[EventType.COOKIE_CONSENT_DECLINED]
    >(
      EventType.COOKIE_CONSENT_DECLINED,
      'ALGOLIA_INSIGHTS_COOKIE_CONSENT_DECLINED_EVENT'
    )?.subscribe((payload) => {
      handleCookieConsentDeclinedEvent(
        payload,
        config,
        algoliaInsightsContextRef
      )
    })

    const cookieConsentGrantedSubscription = subscribeTo<
      EventPayloadType[EventType.COOKIE_CONSENT_GRANTED]
    >(
      EventType.COOKIE_CONSENT_GRANTED,
      'ALGOLIA_INSIGHTS_COOKIE_CONSENT_GRANTED_EVENT'
    )?.subscribe((payload) => {
      handleCookieConsentGrantedEvent(
        payload,
        config,
        algoliaInsightsContextRef
      )
    })

    const cookieConsentDialogOpenedSubscription = subscribeTo<
      EventPayloadType[EventType.COOKIE_CONSENT_DIALOG_OPENED]
    >(
      EventType.COOKIE_CONSENT_DIALOG_OPENED,
      'ALGOLIA_INSIGHTS_COOKIE_CONSENT_DIALOG_OPENED_EVENT'
    )?.subscribe((payload) => {
      handleCookieConsentDialogOpenedEvent(
        payload,
        config,
        algoliaInsightsContextRef
      )
    })

    subscriptionsRef.current = [
      productListViewedSub,
      productClickedSub,
      productViewedSub,
      productAddedToCartSub,
      productAddedToWishlistSub,
      orderCompletedSub,
      collectAtStoreOrderCompletedSub,
      productSubscribedToOutOfStockSub,
      productsFilteredSub,
      articleClickedSub,
      articleViewedSub,
      articleListViewedSub,
      cookieConsentSetSubscription,
      cookieConsentDeclinedSubscription,
      cookieConsentGrantedSubscription,
      cookieConsentDialogOpenedSubscription,
    ].filter((sub): sub is Subscription => sub !== undefined)
  }

  // useEffect only for cleanup on unmount
  useEffect(() => {
    return () => {
      subscriptionsRef.current.forEach((sub) => sub.unsubscribe())
      isSubscribedRef.current = false
    }
  }, [])

  return children
}
